import React, { useEffect, useState } from 'react';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { usersApi, DbUser } from '../api/client';
import { PageHeader } from './layout/PageHeader';
import { TableCard } from './layout/TableCard';
import { tokens } from '../theme/tokens';

export const UserSettings: React.FC = () => {
  const { session } = useCurrentUser();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [me, setMe] = useState<DbUser | null>(null);
  const [username, setUsername] = useState('');

  const refresh = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await usersApi.me();
      const u = (res.data || null) as DbUser | null;
      setMe(u);
      const email = String(session?.user?.email || '').toLowerCase();
      const defaultUsername = email ? email.split('@')[0] : '';
      setUsername(String(u?.username || defaultUsername));
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      setError(null);
      await usersApi.updateMe({ username: username.trim() } as any);
      await refresh();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to update username');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth="md" sx={{ pt: 1, pb: 3 }}>
      <PageHeader title="Settings" subtitle="Profile" />

      {error ? (
        <Alert severity="error" variant="filled" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableCard
          title="Username"
          subtitle="Shown across the app. Default is your email prefix."
          actions={
            <Button size="small" variant="contained" disabled={saving || !username.trim()} onClick={save} sx={{ borderRadius: `${tokens.radius.md}px` }}>
              {saving ? 'Saving…' : 'Save'}
            </Button>
          }
        >
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1.25 }}>
            <TextField label="Username" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth />
            <TextField label="Email" value={me?.email || session?.user?.email || ''} fullWidth disabled />
          </Box>
        </TableCard>
      )}
    </Container>
  );
};


